/**
 * Gheezer Premium Theme - Animation Controller
 *
 * JavaScript for scroll reveal animations, parallax effects, and interactive elements
 */

class PremiumAnimations {
  constructor() {
    this.init();
  }

  init() {
    // Initialize scroll reveal
    this.initScrollReveal();

    // Initialize parallax (if not in reduced motion mode)
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.initParallax();
    }

    // Initialize smooth scroll
    this.initSmoothScroll();

    // Initialize intersection observer for lazy animations
    this.initIntersectionObserver();
  }

  /**
   * Scroll Reveal - Reveals elements as they enter viewport
   */
  initScrollReveal() {
    const animateElements = document.querySelectorAll('.premium-animate');

    if (animateElements.length === 0) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('premium-animate--visible');
          // Optionally unobserve after animation
          // observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    animateElements.forEach(element => {
      observer.observe(element);
    });
  }

  /**
   * Parallax Effect - Creates depth on scroll
   */
  initParallax() {
    const parallaxElements = document.querySelectorAll('.premium-parallax');

    if (parallaxElements.length === 0) return;

    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;

      parallaxElements.forEach(element => {
        const speed = element.dataset.parallaxSpeed || 0.5;
        const yPos = -(scrolled * speed);
        element.style.transform = `translate3d(0, ${yPos}px, 0)`;
      });

      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    });

    // Initial update
    updateParallax();
  }

  /**
   * Smooth Scroll - Smooth scrolling for anchor links
   */
  initSmoothScroll() {
    const anchorLinks = document.querySelectorAll('a[href^="#"]');

    anchorLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');

        // Skip if href is just "#" or empty
        if (!href || href === '#') return;

        const target = document.querySelector(href);

        if (target) {
          e.preventDefault();

          const headerOffset = 80; // Adjust based on sticky header height
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  /**
   * Intersection Observer - Generic observer for lazy loading and animations
   */
  initIntersectionObserver() {
    const lazyElements = document.querySelectorAll('[data-lazy-animate]');

    if (lazyElements.length === 0) return;

    const observerOptions = {
      threshold: 0.2,
      rootMargin: '50px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const animationType = entry.target.dataset.lazyAnimate;
          entry.target.classList.add(`premium-animate--${animationType}`);
          entry.target.classList.add('premium-animate--visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    lazyElements.forEach(element => {
      observer.observe(element);
    });
  }
}

/**
 * Product Gallery - Image zoom and thumbnail selection
 */
class PremiumProductGallery {
  constructor(galleryElement) {
    if (!galleryElement) return;

    this.gallery = galleryElement;
    this.mainImage = this.gallery.querySelector('.premium-product-page__main-image');
    this.thumbnails = this.gallery.querySelectorAll('.premium-product-page__thumbnail');

    this.init();
  }

  init() {
    // Thumbnail click handlers
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchImage(thumbnail);
      });
    });

    // Zoom functionality
    if (this.mainImage) {
      this.mainImage.addEventListener('click', () => {
        this.openLightbox();
      });
    }
  }

  switchImage(thumbnail) {
    const newImageSrc = thumbnail.dataset.fullSrc || thumbnail.src;

    // Update main image
    this.mainImage.style.opacity = '0';

    setTimeout(() => {
      this.mainImage.src = newImageSrc;
      this.mainImage.style.opacity = '1';
    }, 200);

    // Update active thumbnail
    this.thumbnails.forEach(thumb => {
      thumb.classList.remove('premium-product-page__thumbnail--active');
    });
    thumbnail.classList.add('premium-product-page__thumbnail--active');
  }

  openLightbox() {
    // Simple lightbox implementation
    // For production, consider using a library like Photoswipe or GLightbox
    const lightbox = document.createElement('div');
    lightbox.className = 'premium-lightbox';
    lightbox.innerHTML = `
      <div class="premium-lightbox__backdrop"></div>
      <div class="premium-lightbox__content">
        <img src="${this.mainImage.src}" alt="${this.mainImage.alt}">
        <button class="premium-lightbox__close">×</button>
      </div>
    `;

    document.body.appendChild(lightbox);
    document.body.style.overflow = 'hidden';

    // Close handlers
    const closeBtn = lightbox.querySelector('.premium-lightbox__close');
    const backdrop = lightbox.querySelector('.premium-lightbox__backdrop');

    const closeLightbox = () => {
      lightbox.remove();
      document.body.style.overflow = '';
    };

    closeBtn.addEventListener('click', closeLightbox);
    backdrop.addEventListener('click', closeLightbox);

    // Close on escape key
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        closeLightbox();
        document.removeEventListener('keydown', escHandler);
      }
    });
  }
}

/**
 * Variant Selector - Handle variant selection with visual feedback
 */
class PremiumVariantSelector {
  constructor(selectorElement) {
    if (!selectorElement) return;

    this.selector = selectorElement;
    this.options = this.selector.querySelectorAll('.premium-product-page__variant-option');

    this.init();
  }

  init() {
    this.options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectVariant(option);
      });
    });
  }

  selectVariant(selectedOption) {
    // Remove selected class from all options
    this.options.forEach(option => {
      option.classList.remove('premium-product-page__variant-option--selected');
    });

    // Add selected class to clicked option
    selectedOption.classList.add('premium-product-page__variant-option--selected');

    // Trigger variant change event (for Shopify integration)
    const variantId = selectedOption.dataset.variantId;
    if (variantId) {
      const event = new CustomEvent('premium:variantChange', {
        detail: { variantId }
      });
      document.dispatchEvent(event);
    }
  }
}

/**
 * Testimonial Carousel - Auto-rotating testimonials
 */
class PremiumTestimonialCarousel {
  constructor(carouselElement) {
    if (!carouselElement) return;

    this.carousel = carouselElement;
    this.items = this.carousel.querySelectorAll('.premium-testimonial');
    this.currentIndex = 0;
    this.autoRotate = true;
    this.interval = 5000; // 5 seconds

    if (this.items.length > 1) {
      this.init();
    }
  }

  init() {
    // Show first item
    this.showItem(0);

    // Start auto-rotation
    if (this.autoRotate) {
      this.startAutoRotate();
    }

    // Pause on hover
    this.carousel.addEventListener('mouseenter', () => {
      this.stopAutoRotate();
    });

    this.carousel.addEventListener('mouseleave', () => {
      if (this.autoRotate) {
        this.startAutoRotate();
      }
    });
  }

  showItem(index) {
    // Hide all items
    this.items.forEach(item => {
      item.style.opacity = '0';
      item.style.display = 'none';
    });

    // Show current item
    this.items[index].style.display = 'block';
    setTimeout(() => {
      this.items[index].style.opacity = '1';
    }, 50);

    this.currentIndex = index;
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.items.length;
    this.showItem(nextIndex);
  }

  startAutoRotate() {
    this.rotateInterval = setInterval(() => {
      this.next();
    }, this.interval);
  }

  stopAutoRotate() {
    if (this.rotateInterval) {
      clearInterval(this.rotateInterval);
    }
  }
}

/**
 * Initialize all premium animations and components
 */
document.addEventListener('DOMContentLoaded', () => {
  // Initialize main animations
  new PremiumAnimations();

  // Initialize product gallery (if on product page)
  const productGallery = document.querySelector('.premium-product-page__gallery');
  if (productGallery) {
    new PremiumProductGallery(productGallery);
  }

  // Initialize variant selector
  const variantSelector = document.querySelector('.premium-product-page__variant-selector');
  if (variantSelector) {
    new PremiumVariantSelector(variantSelector);
  }

  // Initialize testimonial carousel
  const testimonialCarousel = document.querySelector('[data-premium-carousel]');
  if (testimonialCarousel) {
    new PremiumTestimonialCarousel(testimonialCarousel);
  }
});

/**
 * Export for use in other scripts if needed
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    PremiumAnimations,
    PremiumProductGallery,
    PremiumVariantSelector,
    PremiumTestimonialCarousel
  };
}
